import { Button } from '@heidi/design-system/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@heidi/design-system/dropdown-menu';
import {
  Tooltip,
  TooltipArrow,
  TooltipContent,
  TooltipTrigger,
} from '@heidi/design-system/tooltip';
import { SessionChatSidebarSpotlight } from '@/components/ui/session-ask-heidi/SessionChatSidebarSpotlight';
import { cn } from '@/lib/cn';
import { useTranslations } from '@heidi/runtime/i18n';
import { Ellipsis, Maximize2, Pin, Settings2, Share, SquarePen, Trash2, X } from 'lucide-react';
import React, { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

interface UnauthenticatedActionsProps {
  onSignInClick: () => void;
  onSignUpClick: () => void;
}

export const UnauthenticatedActions = ({
  onSignInClick,
  onSignUpClick,
}: UnauthenticatedActionsProps): JSX.Element => {
  const { t } = useTranslations();

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={onSignInClick}
        className="hidden sm:inline-flex"
      >
        {t('chat.header.logIn')}
      </Button>
      <Button
        variant="default"
        size="sm"
        onClick={onSignUpClick}
        className="hidden sm:inline-flex"
      >
        {t('chat.header.signUp')}
      </Button>
    </>
  );
};

interface CustomizeEvidenceButtonProps {
  hasEvidencePro: boolean;
  onClick: () => void;
}

export const CustomizeEvidenceButton = ({
  hasEvidencePro,
  onClick,
}: CustomizeEvidenceButtonProps): JSX.Element => {
  const { t } = useTranslations();

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          onClick={onClick}
          className="gap-1.5"
          data-testid="customize-evidence-button"
        >
          <Settings2 size={16} />
          <span>{t('chat.header.customizeEvidence')}</span>
        </Button>
      </TooltipTrigger>
      <TooltipContent>
        {hasEvidencePro
          ? t('chat.header.customizeEvidenceTooltip')
          : t('chat.header.customizeEvidenceUpgrade')}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
};

interface NonChatRouteActionsProps {
  onNewChat: () => void;
  onClose: () => void;
  /** When true, the new chat button will be disabled (e.g., already on a new chat) */
  isNewChat?: boolean;
  /** When provided, shows a fullscreen button. Navigates to /scribe/chat/{chatId} or /scribe/chat */
  currentChatId?: string | null;
  /**
   * In-session Evidence expand sidebar: close control uses the short "Close" label
   * (vs "Close chat" elsewhere).
   */
  sessionEvidenceExpandLayout?: boolean;
}

export const NonChatRouteActions = ({
  onNewChat,
  onClose,
  isNewChat = false,
  currentChatId,
  sessionEvidenceExpandLayout = false,
}: NonChatRouteActionsProps): JSX.Element => {
  const { t } = useTranslations();

  const handleOpenFullscreen = useCallback(() => {
    const path = currentChatId ? `/scribe/chat/${currentChatId}` : '/scribe/chat';
    window.open(path, '_blank', 'noopener,noreferrer');
  }, [currentChatId]);

  const closeLabel = sessionEvidenceExpandLayout
    ? t('chat.header.close')
    : t('chat.header.closeChat');

  const fullscreenButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={handleOpenFullscreen}
          className="text-text-secondary"
          aria-label={t('chat.header.viewMode.fullScreen')}
          data-testid="chat-header-fullscreen"
        >
          <Maximize2 size={16} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {t('chat.header.viewMode.fullScreen')}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );

  const closeButton = (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onClose}
          className="text-text-secondary"
          aria-label={closeLabel}
          data-testid="chat-header-close"
        >
          <X size={16} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        {closeLabel}
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );

  return (
    <>
      <Button
        variant="ghost"
        size="sm"
        onClick={onNewChat}
        disabled={isNewChat}
        aria-label={t('chat.newChat')}
        className="gap-1.5 text-text-secondary evidence-panel-narrow:gap-0"
        data-testid="chat-header-new-chat"
      >
        <SquarePen size={16} />
        <span className="evidence-panel-narrow:hidden">{t('chat.newChat')}</span>
      </Button>
      {fullscreenButton}
      {closeButton}
    </>
  );
};

interface ChatOverflowMenuProps {
  showSaveButton: boolean;
  handleSaveToggle: () => void;
  isChatSaved: (chatId: string) => boolean;
  currentChatId?: string | null;
  handleNewChat: () => void;
  /** When provided, shows a destructive "Delete" item last in the menu. */
  onDelete?: () => void;
}

export const ChatOverflowMenu = ({
  showSaveButton,
  handleSaveToggle,
  isChatSaved,
  currentChatId,
  handleNewChat,
  onDelete,
}: ChatOverflowMenuProps): JSX.Element => {
  const { t } = useTranslations();
  const intl = useIntl();
  const isSaved = Boolean(showSaveButton && currentChatId && isChatSaved(currentChatId));
  const saveLabel = isSaved
    ? intl.formatMessage({
        defaultMessage: 'Unpin',
        id: 'TZGA6ywjHd',
        description: 'Evidence chat overflow menu: remove the chat from pinned chats',
      })
    : intl.formatMessage({
        defaultMessage: 'Pin',
        id: '2Ohmb23GuL',
        description: 'Evidence chat overflow menu: pin the chat',
      });

  return (
    <DropdownMenu>
      <Tooltip>
        <TooltipTrigger asChild>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={t('chat.header.moreActions')}
              data-testid="chat-header-overflow-menu"
            >
              <Ellipsis size={16} />
            </Button>
          </DropdownMenuTrigger>
        </TooltipTrigger>
        <TooltipContent side="bottom">
          {t('chat.header.moreActions')}
          <TooltipArrow />
        </TooltipContent>
      </Tooltip>
      <DropdownMenuContent align="end" side="bottom" sideOffset={4} className="min-w-[200px]">
        {showSaveButton && (
          <DropdownMenuItem onClick={handleSaveToggle}>
            <Pin
              size={16}
              className={cn(isSaved && 'fill-current')}
            />
            {saveLabel}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleNewChat}>
          <SquarePen size={16} />
          <FormattedMessage
            defaultMessage="New"
            id="mrkIvnx6KV"
            description="Evidence chat overflow menu: start a new chat"
          />
        </DropdownMenuItem>
        {onDelete && (
          <DropdownMenuItem variant="destructive" onClick={onDelete}>
            <Trash2 size={16} />
            <FormattedMessage defaultMessage="Delete" id="8ETsGIm8K9" description="Evidence chat overflow menu: delete the chat" />
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

interface ShareChatButtonProps {
  onClick: () => void;
}

export const ShareChatButton = ({ onClick }: ShareChatButtonProps): JSX.Element => {
  const intl = useIntl();
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          onClick={onClick}
          aria-label={intl.formatMessage({ defaultMessage: 'Share chat', id: 'bXa+lJhlYV' })}
          data-testid="share-chat-button"
        >
          <Share size={16} />
        </Button>
      </TooltipTrigger>
      <TooltipContent side="bottom">
        <FormattedMessage defaultMessage="Share chat" id="bXa+lJhlYV" />
        <TooltipArrow />
      </TooltipContent>
    </Tooltip>
  );
};

export const LeftHeaderSpotlightWrapper = ({
  enabled,
  children,
}: {
  enabled: boolean;
  children: React.ReactNode;
}) => {
  if (!enabled) return <>{children}</>;
  return <SessionChatSidebarSpotlight>{children}</SessionChatSidebarSpotlight>;
};
